import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { blueGrey, grey } from "@mui/material/colors";
import isNil from "lodash/isNil";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import HtmlTextEditor from "../../components/inputs/HtmlTextEditor";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { formatDateTime } from "../utils/functions/formatDateTime";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";

const CommentItem = ({ createdDate, createdBy, htmlString, px = 1 }) => {
  const user = useSelector((state) => state.user);
  const users = useSelector((state) => state?.users);

  const currentUserEmail = useMemo(
    () => user?.emailAddress,
    [user?.emailAddress]
  );

  const userNameFromEmail = useMemo(() => {
    return getUserFullNameFromEmail(users, createdBy);
  }, [createdBy, users]);

  const isSelf = useMemo(() => {
    return currentUserEmail === createdBy;
  }, [currentUserEmail, createdBy]);

  const styles = useMemo(() => {
    const margin = 4;

    return {
      mr: isSelf ? 0 : margin,
      ml: isSelf ? margin : 0,
      flexGrow: 1,
      justifyContent: isSelf ? "flex-end" : "flex-start",
      gap: 0.8,
    };
  }, [isSelf]);
  return (
    <Flx sx={styles}>
      {isSelf ? null : <UserAvatar isSelf={isSelf} userEmail={createdBy} />}
      <Flx gap={0.5} column sx={{ overflow: "hidden" }}>
        <Flx gap={0.5} jb ac fw wrap>
          <CreatedByTitle>
            {userNameFromEmail}
            <Txt
              secondary
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                pl: 0.6,
                maxWidth: "100%",
              }}
            >
              {`<${createdBy}>`}
            </Txt>
          </CreatedByTitle>
          <CreatedTime>{createdDate}</CreatedTime>
        </Flx>
        <CommentBody isSelf={isSelf}>{htmlString}</CommentBody>
      </Flx>
      {isSelf ? <UserAvatar isSelf={isSelf} userEmail={createdBy} /> : null}
    </Flx>
  );
};

const CreatedByTitle = ({ children }) => {
  const styles = useMemo(() => {
    return {
      fontSize: "11px",
      fontWeight: 400,
      opacity: 0.7,
    };
  }, []);
  return <Txt sx={styles}>{children}</Txt>;
};

const UserAvatar = ({ userEmail, isSelf }) => {
  const users = useSelector((state) => state.users);
  const filteredUserInitials = useMemo(() => {
    const user = users?.find((u) => u.emailAddress === userEmail);
    if (isNil(user)) {
      return null;
    }
    const userName = user?.fullName;
    if (!userName) return "";
    const userNameInitials = userName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase();
    return userNameInitials;
  }, []);

  const styles = useMemo(() => {
    const size = 24;
    return {
      mt: 0.5,
      // mt: 2.7,
      width: size,
      height: size,
      fontSize: `${size - 14}px`,

      backgroundColor: isSelf ? blueGrey[400] : grey[400],
    };
  }, []);

  return <Avatar sx={styles}>{filteredUserInitials}</Avatar>;
};

const CreatedTime = ({ children }) => {
  const theme = useTheme();
  if (isNil(children)) {
    return null;
  }
  return (
    <Txt
      body2
      sx={{
        fontWeight: 400,
        opacity: 0.6,
        color: theme.palette.text.secondary,
        fontSize: "10px",
      }}
    >
      {formatDateTime(children, "time")}
    </Txt>
  );
};

const CommentBody = ({ children, isSelf }) => {
  const styles = useMemo(() => {
    return {
      borderRadius: 2,
      borderTopLeftRadius: isSelf ? "8px" : 0,
      borderTopRightRadius: isSelf ? 0 : "8px",
      py: 0.5,
      px: 1.5,
      backgroundColor: isSelf ? "#e3f2fd8c" : grey[100],
      maxHeight: "580px",
      overflowY: "auto",
    };
  }, [isSelf]);

  if (isNil(children)) {
    return null;
  }

  return (
    <Box sx={styles}>
      <HtmlTextEditor
        value={children}
        readOnly
        placeholder="Start typing here..."
      />
    </Box>
  );
};

export default CommentItem;
